import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Col, Row, Select, Space, Statistic, Table, Tag, Typography, message,
} from 'antd';
import { getAdminUserCropsApi } from '../../api/admin';
import { AREA_UNITS, CROP_NAME_IMAGE } from '../../utils/constants';
import { formatCurrency, formatDate } from '../../utils/formatters';

const { Text } = Typography;

const CURRENT_YEAR = new Date().getFullYear();
export const YEAR_OPTIONS = Array.from({ length: CURRENT_YEAR - 2018 + 2 }, (_, i) => CURRENT_YEAR + 1 - i)
  .map((y) => ({ value: y, label: String(y) }));

// All crops of one user, for admin screens. highlightCommonId tints the rows of
// the crop the admin arrived from (e.g. the Crop Growers page).
export default function UserCropsTable({ userId, initialYear = null, highlightCommonId = null }) {
  const [loading, setLoading] = useState(false);
  const [crops, setCrops]     = useState([]);
  const [year, setYear]       = useState(initialYear);

  useEffect(() => { setYear(initialYear); }, [userId, initialYear]);

  const fetchCrops = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const params = { user_id: userId };
      if (year) params.year = year;
      const res = await getAdminUserCropsApi(params);
      setCrops(res.data?.data || []);
    } catch {
      message.error('Failed to load crops');
      setCrops([]);
    } finally {
      setLoading(false);
    }
  }, [userId, year]);

  useEffect(() => { fetchCrops(); }, [fetchCrops]);

  const isHighlighted = (r) => highlightCommonId != null && String(r.common_id) === String(highlightCommonId);
  const totalExpense  = crops.reduce((s, c) => s + Number(c.kharch || 0), 0);
  const totalSale     = crops.reduce((s, c) => s + Number(c.vechan || 0), 0);
  const activeCount   = crops.filter((c) => Number(c.status) === 1).length;

  const columns = [
    {
      title: 'Crop',
      key: 'crop',
      render: (_, r) => (
        <Space>
          {r.image && (
            <img
              src={CROP_NAME_IMAGE(r.image)}
              alt={r.crop}
              style={{ width: 32, height: 32, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
            />
          )}
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>{r.crop || '-'}</div>
            {r.verity && <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.verity}</div>}
          </div>
        </Space>
      ),
    },
    {
      title: 'Farm / Plot',
      key: 'farm',
      render: (_, r) => (
        <div>
          <div style={{ fontSize: 13 }}>{r.farm_name || '-'}</div>
          {r.plot_name && <div style={{ fontSize: 11, color: '#9ca3af' }}>{r.plot_name}</div>}
        </div>
      ),
    },
    {
      title: 'Area',
      key: 'area',
      width: 110,
      render: (_, r) => (r.area ? `${r.area} ${AREA_UNITS[r.area_unit] || ''}` : '-'),
    },
    { title: 'Sown', dataIndex: 'crop_date', width: 110, render: formatDate },
    { title: 'Harvest', dataIndex: 'harvest_date', width: 110, render: formatDate },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (v) => (Number(v) === 1
        ? <Tag color="green">Active</Tag>
        : <Tag color="default">Harvested</Tag>),
    },
    {
      title: 'Expense',
      dataIndex: 'kharch',
      width: 110,
      align: 'right',
      render: (v) => <Text style={{ color: '#D8583D' }}>{formatCurrency(v || 0)}</Text>,
    },
    {
      title: 'Sale',
      dataIndex: 'vechan',
      width: 110,
      align: 'right',
      render: (v) => <Text style={{ color: '#4ebf53' }}>{formatCurrency(v || 0)}</Text>,
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {[
          { label: 'Crops',         value: crops.length,               color: '#1a6b2a', bg: '#f6ffed' },
          { label: 'Active',        value: activeCount,                color: '#1677ff', bg: '#f0f5ff' },
          { label: 'Total Expense', value: formatCurrency(totalExpense), color: '#D8583D', bg: '#fff2f0' },
          { label: 'Total Sale',    value: formatCurrency(totalSale),    color: '#4ebf53', bg: '#f6ffed' },
        ].map((item) => (
          <Col xs={12} sm={6} key={item.label}>
            <Card style={{ borderRadius: 8, background: item.bg, border: 'none' }} styles={{ body: { padding: 12 } }}>
              <Statistic title={item.label} value={item.value} valueStyle={{ color: item.color, fontSize: 18, fontWeight: 'bold' }} />
            </Card>
          </Col>
        ))}
      </Row>

      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <Select
          value={year}
          onChange={(v) => setYear(v ?? null)}
          options={YEAR_OPTIONS}
          placeholder="All years"
          allowClear
          style={{ width: 140 }}
          size="small"
        />
      </div>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={crops}
        loading={loading}
        size="small"
        scroll={{ x: 900 }}
        onRow={(r) => (isHighlighted(r) ? { style: { background: '#fffbe6' } } : {})}
        pagination={{ pageSize: 20, showSizeChanger: false, showTotal: (t) => `${t} crops` }}
      />
    </div>
  );
}
